/**
 * Hard daily ceilings on parcel/address searches.
 *
 * These are deliberately separate from the burst rate limits in
 * `public-rate-limit.ts`. Rate limits stop a fast script; these stop a patient
 * one that probes a county slowly, all day, for free.
 *
 * Client-safe: enforcement lives in `public-rate-limit.server.ts`.
 */

export type SearchAudience = "anonymous" | "free" | "starter" | "pro" | "team" | "degraded";

/**
 * Searches allowed per UTC calendar day. Tune here — nowhere else reads raw
 * numbers.
 *
 * `degraded` applies when the human-verification provider was unreachable and
 * the visitor was let through on trust: they still get to search, but only a
 * handful of times.
 */
export const DAILY_SEARCH_CAPS: Record<SearchAudience, number> = {
  anonymous: 2,
  free: 5,
  starter: 50,
  pro: 100,
  team: 200,
  degraded: 2,
};

/** Start of the UTC day containing `nowMs`. */
export function dayStartMs(nowMs: number): number {
  return Math.floor(nowMs / 86_400_000) * 86_400_000;
}

/** When the UTC day containing `nowMs` rolls over. */
export function dayResetMs(nowMs: number): number {
  return dayStartMs(nowMs) + 86_400_000;
}

/**
 * The sentence shown when someone hits the ceiling. Never a bare error — each
 * audience gets the next step that actually helps them.
 */
export function dailyCapMessage(audience: SearchAudience, cap: number): string {
  switch (audience) {
    case "anonymous":
      return `You've used all ${cap} free property searches for today from this device. Create a free account to keep searching. It takes a few seconds and raises the limit.`;
    case "free":
      return `You've used all ${cap} property searches on your free account today. Your searches reset tomorrow, or you can upgrade for a much higher daily limit.`;
    case "starter":
    case "pro":
    case "team":
      return `You've reached today's limit of ${cap} property searches on your ${audience} plan. This ceiling exists to protect the public data sources we rely on. Get in touch if you need more.`;
    case "degraded":
      return `We couldn't finish verifying this browser, so searches are temporarily limited to ${cap} per day. Reload the page to try verifying again.`;
  }
}

/**
 * True when an error message came from the daily-cap gate. Lets the UI swap a
 * generic error for the dedicated "reset tomorrow / upgrade" banner.
 */
export function isDailyCapMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  return /property searches|today's limit of \d+ property searches|searches are temporarily limited/i.test(
    message,
  );
}
